import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { TablePagination } from "@mui/material";
import { ApiService, CoveragePhaseService } from "../../../../api";
import {
	SidePanel,
	SidePanelContent,
	CustomButton,
	CircularLoader,
	icon,
	HorizontalDivider,
	IconComponent,
} from "../../../../common/components";
import { translate } from "../../../../common/providers";
import CoverageDocumentService from "../../../../api/services/operation/coverage-document-service";
import styles from "./AssociatedDeliverablesPanel.module.css";

const AssociatedDeliverablesPanel = ({ open, onClose, phaseId, onOpenList, imported }) => {
	const projectId = useSelector(({ context }) => context.project?.id);

	const [phases, setPhases] = useState([]);
	const [importData, setImportData] = useState([]);
	const [leftDeliverables, setLeftDeliverables] = useState([]);
	const [rightDeliverables, setRightDeliverables] = useState([]);
	const [selectedLeft, setSelectedLeft] = useState(null);
	const [isLoadingLeft, setIsLoadingLeft] = useState(false);
	const [isLoadingRight, setIsLoadingRight] = useState(false);
	const [leftPage, setLeftPage] = useState(0);
	const [rightPage, setRightPage] = useState(0);
	const [leftPerPage, setLeftPerPage] = useState(15);
	const [rightPerPage, setRightPerPage] = useState(15);

	const cancelTokenRef = useRef(null);
	const shouldAutoSelectRef = useRef(true);

	useEffect(() => {
		if (!open || !projectId || !phaseId || !imported) {
			shouldAutoSelectRef.current = true;
			return undefined;
		}

		cancelTokenRef.current = ApiService.getCancelTokenSource();

		CoveragePhaseService.getAllByProject({ projectId }, cancelTokenRef.current.token)
			.then((data) => {
				const items = data?.content || data?.contents || data || [];
				setPhases(items);
			})
			.catch(console.error);

		CoveragePhaseService.getImportStrategies({ projectId, phaseId }, cancelTokenRef.current.token)
			.then((strategies) => {
				const list = Array.isArray(strategies) ? strategies : strategies?.content || strategies?.contents || [];

				setImportData(list);

				if (list.length === 0) {
					setLeftDeliverables([]);
					setIsLoadingLeft(false);
					return;
				}

				const sourceDeliverableIds = [...new Set(list.map((s) => s.sourceDeliverableId).filter(Boolean))];

				if (sourceDeliverableIds.length === 0) {
					setLeftDeliverables([]);
					setIsLoadingLeft(false);
					return;
				}

				setIsLoadingLeft(true);

				CoverageDocumentService.getDeliverablesByCatFilter(
					{ projectId },
					{
						deliverables: {
							is: true,
							matchAll: false,
							elements: sourceDeliverableIds,
						},
					},
					cancelTokenRef.current.token,
					{ page: 0, limit: 50 }
				)
					.then((leftData) => {
						let leftItems = [];
						if (Array.isArray(leftData)) {
							leftItems = leftData;
						} else if (leftData?.content) {
							leftItems = leftData.content;
						} else if (leftData?.contents) {
							leftItems = leftData.contents;
						}

						const validIds = new Set(sourceDeliverableIds.map((id) => String(id)));
						const filtered = leftItems.filter((d) => d && validIds.has(String(d.id)));

						setLeftDeliverables(filtered);

						if (filtered.length > 0 && shouldAutoSelectRef.current) {
							setSelectedLeft(filtered[0].id);
							shouldAutoSelectRef.current = false;
						}
					})
					.catch((error) => {
						console.error("Error fetching left deliverables:", error);
						setLeftDeliverables([]);
					})
					.finally(() => setIsLoadingLeft(false));
			})
			.catch((error) => {
				console.error("Error fetching import strategies:", error);
				setIsLoadingLeft(false);
			});

		return () => {
			if (cancelTokenRef.current) {
				ApiService.cancelTokens(cancelTokenRef.current);
			}
		};
	}, [open, projectId, phaseId, imported]);

	useEffect(() => {
		if (!selectedLeft || !importData.length || !projectId || !phaseId) {
			setRightDeliverables([]);
			setIsLoadingRight(false);
			return;
		}

		const selectedMapping = importData.find((m) => String(m.sourceDeliverableId) === String(selectedLeft));

		if (!selectedMapping) {
			setRightDeliverables([]);
			setIsLoadingRight(false);
			setRightPage(0);
			return;
		}

		const targetIds = Array.isArray(selectedMapping.targetDeliverableIds)
			? selectedMapping.targetDeliverableIds
			: [];

		if (targetIds.length === 0) {
			setRightDeliverables([]);
			setIsLoadingRight(false);
			setRightPage(0);
			return;
		}

		setRightPage(0);
		setIsLoadingRight(true);

		CoverageDocumentService.getDeliverablesByCatFilter(
			{ projectId },
			{
				deliverables: {
					is: true,
					matchAll: false,
					elements: targetIds,
				},
			},
			cancelTokenRef.current?.token,
			{ page: 0, limit: 50 }
		)
			.then((rightData) => {
				let rightItems = [];
				if (Array.isArray(rightData)) {
					rightItems = rightData;
				} else if (rightData?.content) {
					rightItems = rightData.content;
				} else if (rightData?.contents) {
					rightItems = rightData.contents;
				}

				const validIds = new Set(targetIds.map((id) => String(id)));
				const filtered = rightItems.filter((item) => item && validIds.has(String(item.id)));

				setRightDeliverables(filtered);
			})
			.catch((error) => {
				console.error("Error fetching right deliverables:", error);
				setRightDeliverables([]);
			})
			.finally(() => setIsLoadingRight(false));
	}, [selectedLeft, importData, projectId, phaseId]);

	const handleClose = () => {
		setSelectedLeft(null);
		setLeftDeliverables([]);
		setRightDeliverables([]);
		setImportData([]);
		shouldAutoSelectRef.current = true;
		onClose();
	};

	const handleLeftItemKeyDown = (e, id) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			setSelectedLeft(id);
		}
	};

	const leftTotal = leftDeliverables.length;
	const rightTotal = rightDeliverables.length;
	const leftStart = leftPage * leftPerPage;
	const rightStart = rightPage * rightPerPage;
	const leftPageItems = leftDeliverables.slice(leftStart, leftStart + leftPerPage);
	const rightPageItems = rightDeliverables.slice(rightStart, rightStart + rightPerPage);

	const leftListContent = (() => {
		if (isLoadingLeft) {
			return (
				<div className={styles.loader}>
					<CircularLoader size={24} />
				</div>
			);
		}
		if (!importData.length) {
			return <div className={styles.loader}>{translate("project-phases.no-import-data")}</div>;
		}
		return (
			<>
				{leftPageItems.map((d) => (
					<div
						key={d.id}
						className={`${styles.listItem} ${selectedLeft === d.id ? styles.selectedItem : ""}`}
						role="button"
						tabIndex={0}
						onClick={() => setSelectedLeft(d.id)}
						onKeyDown={(e) => handleLeftItemKeyDown(e, d.id)}
					>
						<span className={styles.listItemLabel} title={d.name}>
							{d.name}
						</span>
					</div>
				))}
			</>
		);
	})();

	const sourcePhaseName = (() => {
		if (!importData.length) {
			return "-";
		}
		const first = importData[0];
		return phases.find((p) => p.id === first.sourcePhaseId)?.name || "-";
	})();

	const targetPhaseName = (() => {
		if (!importData.length) {
			return "-";
		}
		const first = importData[0];
		return phases.find((p) => p.id === first.targetPhaseId)?.name || "-";
	})();

	const formatStrategyDate = () => {
		let dateObj = new Date();

		if (importData.length > 0 && importData[0].created) {
			dateObj = new Date(importData[0].created);
		}

		const date = dateObj.toLocaleDateString("fr-FR", {
			weekday: "long",
			day: "numeric",
			month: "long",
			year: "numeric",
		});

		const time = dateObj
			.toLocaleTimeString("fr-FR", {
				hour: "numeric",
				minute: "2-digit",
				hour12: false,
			})
			.replace(":", "h");

		const dateParts = date.split(" ");
		const year = dateParts.pop();
		const dayMonth = dateParts.join(" ");

		return { dayMonth, year, time };
	};

	const strategyDate = formatStrategyDate();

	return (
		<SidePanel open={open} size={55} onClose={handleClose}>
			<SidePanelContent title={translate("project-phases.header.deliverables")} onClose={handleClose}>
				<div className={styles.listAccessWrapper}>
					<div className={styles.listAccessCard}>
						<div className={styles.listAccessHeader}>
							{translate("project-phases.associated.deliverables.list")}
						</div>
						<div className={styles.listAccessBody}>
							<CustomButton variant="outlined" onClick={() => onOpenList && onOpenList(phaseId)}>
								{translate("project-phases.access.associated.deliverables")}
							</CustomButton>
						</div>
					</div>
				</div>
				{imported && (
					<div className={styles.contentWrapper}>
						<div className={styles.importContainer}>
							<div className={`${styles.panel} ${styles.leftPanel}`}>
								<div className={styles.header}>
									<div className={styles.headerContent}>
										{importData.length > 0 && (
											<span className={styles.strategyTitle}>
												{translate("project-phases.associated.strategy-heritage-title")}
											</span>
										)}
										<span className={styles.headerTitle}>
											{translate("project-phases.associated.import-phase-title")}
										</span>
									</div>
								</div>
								<div className={styles.controls}>
									<span className={styles.phaseName}>{sourcePhaseName}</span>
									<span className={styles.badge}>
										{translate("project-phases.deliverables.count", { count: leftTotal })}
									</span>
								</div>
								<HorizontalDivider className={`${styles.panelDivider} ${styles.leftDivider}`} />
								<div className={styles.listWrapper}>
									<div className={styles.listContainer}>{leftListContent}</div>
									{leftTotal > 0 && (
										<TablePagination
											component="div"
											count={leftTotal}
											labelRowsPerPage={translate("common:pagination.row-per-page")}
											page={leftPage}
											rowsPerPage={leftPerPage}
											rowsPerPageOptions={[15, 25, 50]}
											onPageChange={(_, newPage) => setLeftPage(newPage)}
											onRowsPerPageChange={(e) => {
												setLeftPerPage(Number(e.target.value));
												setLeftPage(0);
											}}
										/>
									)}
								</div>
							</div>
							<div className={styles.arrow}>
								<div className={styles.arrowIcon}>
									<IconComponent color="var(--color-white)" icon={icon.faArrowRight} size="sm" />
								</div>
							</div>
							<div className={`${styles.panel} ${styles.rightPanel}`}>
								<div className={styles.header}>
									<div className={styles.headerContent}>
										{importData.length > 0 && (
											<span className={styles.strategyDate}>
												<span className={styles.strategyDateMain}>{strategyDate.dayMonth}</span>
												<span className={styles.strategyDateSecondary}>
													{strategyDate.year} à {strategyDate.time}
												</span>
											</span>
										)}
										<span className={styles.headerTitle}>
											{translate("project-phases.associated.target-phase-title")}
										</span>
									</div>
								</div>
								<div className={styles.controls}>
									<span className={styles.phaseName}>{targetPhaseName}</span>
									<span className={styles.badge}>
										{translate("project-phases.deliverables.count", { count: rightTotal })}
									</span>
								</div>
								<HorizontalDivider className={`${styles.panelDivider} ${styles.rightDivider}`} />
								<div className={styles.listWrapper}>
									<div className={styles.listContainer}>
										{!selectedLeft && (
											<div className={styles.selectedTargetInfo}>
												<span>
													{translate(
														"project-phases.strategy-import.select-source-deliverable"
													)}
												</span>
											</div>
										)}
										{selectedLeft && isLoadingRight && (
											<div className={styles.loader}>
												<CircularLoader size={24} />
											</div>
										)}
										{selectedLeft && !isLoadingRight && rightPageItems.length === 0 && (
											<div className={styles.selectedTargetInfo}>
												<span>No associated deliverables</span>
											</div>
										)}
										{selectedLeft &&
											!isLoadingRight &&
											rightPageItems.length > 0 &&
											rightPageItems.map((d) => (
												<div key={d.id} className={styles.listItem}>
													<span className={styles.listItemLabel} title={d.name}>
														{d.name}
													</span>
												</div>
											))}
									</div>
									{rightTotal > 0 && (
										<TablePagination
											component="div"
											count={rightTotal}
											labelRowsPerPage={translate("common:pagination.row-per-page")}
											page={rightPage}
											rowsPerPage={rightPerPage}
											rowsPerPageOptions={[15, 25, 50]}
											onPageChange={(_, newPage) => setRightPage(newPage)}
											onRowsPerPageChange={(e) => {
												setRightPerPage(Number(e.target.value));
												setRightPage(0);
											}}
										/>
									)}
								</div>
							</div>
						</div>
					</div>
				)}
			</SidePanelContent>
		</SidePanel>
	);
};

export default AssociatedDeliverablesPanel;
